import React, { useCallback, useState } from "react";
import trashIcon from "@/assets/icons/trash.svg";
import eyeIcon from "@/assets/icons/eye.svg";
import bookImage from "@/assets/images/book.png";
import { Pagination, Skeleton } from "@mui/material";
import DeleteModal from "@/components/DeleteModal/DeleteModal";
import { IBook } from "@/store/models/books.models";
import {
  useDeleteBookMutation,
  useGetBooksQuery,
} from "@/store/rtk-api/books.api";
import styles from "./BooksContent.module.scss";
import { ViewBookModal } from "../ViewBookModal/ViewBookModal";
import useItemsPerPage from "@/hooks/useItemsPerPage";

const BooksContent = () => {
  const [page, setPage] = useState(1);
  const [isOpenDeleteModal, setIsOpenDeleteModal] = useState(false);
  const [isOpenViewModal, setIsOpenViewModal] = useState(false);
  const [selectedBook, setSelectedBook] = useState<IBook | null>(null);
  const itemsPerPage = useItemsPerPage(2, 4, 6, 1050, 1470, 8, 1735);

  const {
    data: booksData,
    refetch,
    isLoading,
    isSuccess,
  } = useGetBooksQuery({ page, limit: itemsPerPage, sortByDate: "ASC" });

  const [deleteBookByUid] = useDeleteBookMutation();

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ) => {
    setPage(value);
  };

  const handleToggleDeleteModal = (book: IBook) => {
    setSelectedBook(book);
    setIsOpenDeleteModal((prev) => !prev);
  };

  const handleToggleViewModal = (book: IBook) => {
    setSelectedBook(book);
    setIsOpenViewModal((prev) => !prev);
  };

  const handleDeleteBook = useCallback(async () => {
    if (selectedBook) {
      await deleteBookByUid(selectedBook.uid);
      setIsOpenDeleteModal(false);
      refetch();
    }
  }, [selectedBook, deleteBookByUid, refetch]);

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        <div className={styles.itemsContainer}>
          {isLoading &&
            Array(3)
              .fill(0)
              .map((_, index) => (
                <React.Fragment key={index}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={200}
                    animation="wave"
                    sx={{ borderRadius: "8px" }}
                  />
                </React.Fragment>
              ))}
          {isSuccess && booksData.total > 0 ? (
            booksData.data.map((book) => (
              <div className={styles.item} key={book.uid}>
                <img
                  className={styles.itemBookImg}
                  src={book.imgs.length ? book.imgs[0] : bookImage}
                  alt="book"
                />
                <p className={styles.itemDescr}>{book.description}</p>
                <div className={styles.itemBtns}>
                  <button
                    className={styles.itemReviewBtn}
                    onClick={() => handleToggleViewModal(book)}
                  >
                    <img
                      className={styles.itemBtnImg}
                      src={eyeIcon}
                      alt="review"
                    />
                  </button>
                  <button
                    className={styles.itemDeleteBtn}
                    onClick={() => handleToggleDeleteModal(book)}
                  >
                    <img
                      className={styles.itemBtnImg}
                      src={trashIcon}
                      alt="delete"
                    />
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.noDataText}>There is no data yet</div>
          )}
        </div>
        {isOpenDeleteModal && (
          <DeleteModal
            isOpen={isOpenDeleteModal}
            setIsOpen={setIsOpenDeleteModal}
            name={
              selectedBook && selectedBook.title ? selectedBook.title : "book"
            }
            supportingText="Are you sure you want to delete this book?"
            onDelete={handleDeleteBook}
          />
        )}

        {isOpenViewModal && selectedBook && (
          <ViewBookModal
            isOpen={isOpenViewModal}
            setIsOpen={setIsOpenViewModal}
            selectedBook={selectedBook}
          />
        )}

        {isSuccess && (
          <Pagination
            page={page}
            onChange={handlePageChange}
            count={Math.ceil(booksData.total / itemsPerPage)}
          />
        )}
      </div>
    </div>
  );
};

export default BooksContent;
